/**
 * 统一订单相关接口 + 类型定义
 * 对应 Home端 - 订单模块
 */

import { http } from "@/http/axios";

// ==================== 产品类型 ====================

export type OrderProductType = "car" | "hotel" | "tour" | "guide" | "expert";

// ==================== 订单状态 ====================

/** API 状态枚举 */
export const OrderStatusMap = {
  pending: "待支付",
  confirming: "待商家确认",
  signed: "待签约",
  traveling: "待出行",
  completed: "已完成",
  aftersale: "售后中",
} as const;
export type OrderStatus = keyof typeof OrderStatusMap;

/** 主订单状态（用于前端展示） */
export type MainOrderStatus =
  | "待支付"
  | "待确认"
  | "待商家确认"
  | "待签约"
  | "待入住"
  | "待出行"
  | "使用中"
  | "已入住"
  | "部分履约"
  | "全部履约"
  | "已完成"
  | "部分取消"
  | "全额取消"
  | "售后中"
  | "已取消"
  | "退款中";

/** 子订单状态类型 */
export type HotelSubStatus =
  | "待酒店确认"
  | "已确认·待入住"
  | "已入住"
  | "已离店"
  | "已完成"
  | "已取消"
  | "售后中";
export type CarSubStatus =
  | "待派车"
  | "待取车"
  | "使用中"
  | "已还车"
  | "已完成"
  | "已取消"
  | "售后中";
export type RouteSubStatus =
  | "待成团"
  | "已成团·待出发"
  | "行程中"
  | "已完成"
  | "已完结"
  | "已取消"
  | "售后中";
export type GuideSubStatus =
  | "待确认"
  | "已确认·待服务"
  | "服务中"
  | "已完成"
  | "已取消"
  | "售后中";

// ==================== 订单数据结构 ====================

/** 租车验车照片 */
export interface CarInspectionPhoto {
  label: string;
  url: string;
}

/** 子订单 */
export interface SubOrder {
  id: string;
  type: "hotel" | "car" | "tour" | "guide";
  pickupPhotos?: CarInspectionPhoto[];
  returnPhotos?: CarInspectionPhoto[];
  driverName?: string;
  driverIdCard?: string;
  driverLicense?: string;
}

/** 售后信息对象（refund_info） */
export interface RefundInfo {
  has_refund: number; // 是否有售后记录：0=无 1=有
  refund_id: number; // 售后记录ID
  refund_no: string; // 售后单号
  refund_status: number; // 售后状态：0待处理 10已驳回 20已退款 30已取消
  refund_status_text: string; // 售后状态文字（如"已拒绝"）
  apply_desc: string; // 申请描述/说明
  apply_img: string[]; // 申请凭证图片列表
  apply_reason: string; // 申请原因分类
  create_time: string; // 申请时间
  refund_money: string; // 售后退款金额
  refuse_desc: string; // 拒绝原因（仅已驳回时有值）
}

/** 售后 refund_status → 展示文案（列表与各订单详情统一） */
export const REFUND_STATUS_MAP: Record<number, string> = {
  0: "售后待处理",
  10: "售后已驳回",
  20: "已退款",
  30: "售后已取消",
};

/** 订单（组件通用） */
export interface Order {
  id: string;
  orderNo?: string;
  type: "multi" | "single";
  mainStatus: MainOrderStatus;
  mainStatusStep: number;
  total: number;
  time: string;
  destination: string;
  has_comment: number;
  has_invoice: number;
  invoice_status: number | null; // 0 申请中  2 已开票
  cover_url?: string;
  travelers: number;
  subOrders?: SubOrder[];
  productType?: "hotel" | "car" | "tour" | "guide";
  productIcon?: string;
  productName?: string;
  productSpec?: string;
  productDate?: string;
  productSubStatus?: string;
  productSubStatusColor?: string;
  productSubStatusBg?: string;
  remark?: string;
  hasRefund?: number; // 是否有售后：0=无 1=有
  refundStatus?: number; // 售后状态：0待处理 10已驳回 20已退款 30已取消
  refundInfo?: RefundInfo; // 完整售后信息对象
  /** 是否达人订单（使用6步进度条：接单→签约→出发→到达→服务→完成） */
  isExpert?: boolean;
}

/** 主状态步进点 */
/**
 * 主状态步进点（按类型 + 数字 status 码对齐）
 *
 * hotel: 0=待支付 →1=待确认 →2=待入住 →(4已入住)→7=已完成
 * tour:  0=待支付 →1=待商家确认 →2=待签约 →3=待出行 →(4使用中)→7=已完成
 */
export function getMainStatusSteps(type?: string): string[] {
  if (type === "tour" || type === "guide") {
    return ["待支付", "待确认", "待签约", "待出行", "已完成"];
  }
  if (type === "car") {
    return ["待支付", "待确认", "待签约", "待派车", "已完成"];
  }
  // hotel : 没有待签约，第3步是待入住
  return ["待支付", "待确认", "待入住", "已完成"];
}

/**
 * 达人(guide/expert)订单专用进度步骤（与 BLW 达人接口 status 对齐）
 *
 * 6 步流程：
 *   1.待支付(status=0) → 2.待商家确认(status=10) → 3.合同签约(status=13) →
 *   4.待出行(status=16) → 5.待服务(status=20/30) → 6.服务完成(status>=40/-10)
 */
export function getExpertOrderSteps(): string[] {
  return ["待支付", "待商家确认", "合同签约", "待出行", "待服务", "服务完成"];
}

/**
 * 达人订单状态码 → 进度条当前 step 映射
 * 对应 staff.ts 中 status(-20~-1,0,10,13,16,20,30,40,50)
 *
 * 返回值含义（共 6 步，step 范围 0~6）：
 *   step=0: 第1步未完成（落在"待支付"）
 *   step=1: 第1步完成（"待支付"亮起），当前在第2步
 *   step=6: 全部完成
 *   step=-1: 异常状态（已退款/已取消等），不展示进度条
 */
export function resolveExpertOrderStep(
  status: number,
  _isDepart?: number,
): number {
  const st = Number.isFinite(status) ? Math.trunc(status) : 0;

  // 终态：不展示进度
  if (st === -1 || st === -20) return -1;
  // 已退款/待评价/已完成 → 全部6步完成
  if (st >= 40 || st === -10) return 6;
  // 进行中(服务中) → 前5步完成，落在第6步前
  if (st >= 30) return 5;
  // 待服务(status=20) → 前4步完成
  if (st >= 20) return 4;
  // 待出发(status=16) → 前3步完成
  if (st === 16) return 3;
  // 待签约(status=13) → 前2步完成
  if (st === 13) return 2;
  // 待接单(status=10) → 第1步完成
  if (st === 10) return 1;
  // 待付款(status=0) → 落在第1步
  return 0;
}

/** 状态标签样式映射 */
export const statusStyle: Record<
  string,
  { color: string; bg: string; note?: string }
> = {
  // C端通用状态
  待支付: { color: "#D97706", bg: "#FEF3C7" },
  待确认: { color: "#6366F1", bg: "#EEF2FF" },
  待签约: { color: "#0EA5E9", bg: "#E0F2FE" },
  待出行: { color: "#2563EB", bg: "#DBEAFE", note: "所有服务待使用" },
  部分履约: { color: "#1E40AF", bg: "#DBEAFE" },
  全部履约: { color: "#1E3A8A", bg: "#DBEAFE", note: "所有服务已完成，待结算" },
  已完成: { color: "#059669", bg: "#D1FAE5", note: "订单已结算，可评价" },
  部分取消: { color: "#EA580C", bg: "#FFEDD5" },
  全额取消: {
    color: "#EA580C",
    bg: "#FFEDD5",
    note: "订单已全额取消，可查看详情",
  },
  售后中: { color: "#DC2626", bg: "#FEE2E2" },
  已取消: { color: "#9CA3AF", bg: "#F3F4F6" },
  退款中: { color: "#DC2626", bg: "#FEE2E2" },
  // 售后专用状态（refund_status 映射）
  售后待处理: { color: "#DC2626", bg: "#FEE2E2" },
  售后已驳回: { color: "#DC2626", bg: "#FEE2E2" },
  售后已取消: { color: "#9CA3AF", bg: "#F3F4F6" },

  // 达人(expert/guide)专用状态（status 数值 → 中文，对应 staff.ts）
  待接单: { color: "#FFA726", bg: "#FFF8E1" },       // status=10
  合同签约: { color: "#42A5F5", bg: "#E3F2FD" },       // status=13
  待出发: { color: "#0EA5E9", bg: "#E0F2FE" },         // status=16
  待服务: { color: "#FE7A3A", bg: "#FFF3E0" },         // status=20
  进行中: { color: "#66BB6A", bg: "#E8F5E9" },         // status=30
  待评价: { color: "#AB47BC", bg: "#F3E5F5" },         // status=40
  已退款: { color: "#9E9E9E", bg: "#FAFAFA" },          // status=-20
  退款申请: { color: "#FF7043", bg: "#FBE9E7" },       // status=-10
};

/**
 * 达人(guide/expert)订单 status 数值 → 中文状态文字
 * 对应 staff.ts 中 status(-20,-10,-1,0,10,13,16,20,30,40,50)
 */
export function resolveExpertOrderStatusText(
  status: number,
): MainOrderStatus {
  const s = Number.isFinite(status) ? Math.trunc(status) : 0;
  switch (s) {
    case -20:
      return "已退款";
    case -10:
      return "退款申请";
    case -1:
      return "已取消";
    case 0:
      return "待付款";
    case 10:
      return "待接单";
    case 13:
      return "待签约";
    case 16:
      return "待出发";
    case 20:
      return "待服务";
    case 30:
      return "进行中";
    case 40:
      return "待评价";
    case 50:
      return "已完成";
    default:
      return "未知";
  }
}

// ==================== API 类型 ====================

/** 统一订单列表项（API 响应）— 对应 GET /home/order/all */
export interface OrderListItem {
  id: number;
  order_no: string;
  type: OrderProductType;
  has_comment: number; // 1已经评论 0未评论
  has_invoice: number; // 1已申请发票 0未申请发票
  invoice_status: number | null; // 0 申请中  2 已开票
  type_text: string; // 类型中文：线路/酒店/租车/导游
  remark: string; // 备注信息（如"5天4晚"、"豪华套房"）
  product_title: string; // 产品标题
  product_id: number;
  cover_url?: string; // 封面图片URL
  total_amount: string | number;
  status: number; // 状态码（数字） // type 为hotel 时 0待支付 1已支付(待确认) 2已确认(待入住) 4确认入住(已入住) 5已取消 6已退款  7已完成(已离店) type 为tour 时  0=待支付 1=已支付(待商家确认) 2=已接单（待上传合同） 3=待出行 4=已出行（使用中） 5=已取消  6=已退款 7已完成
  status_text: string; // 状态文字
  start_date: string; // 开始日期
  end_date?: string; // 结束日期
  days?: number; // 天数
  nights?: number; // 晚数
  total_people?: number; // 总人数
  created_at: string; // 创建时间
  pay_time?: string; // 支付时间
  // 售后相关字段
  has_refund?: number; // 是否有售后记录：0=无 1=有
  refund_id?: number; // 售后记录ID
  refund_status?: number; // 售后状态
  refund_status_text?: string; // 售后状态文字
  refund_money?: string; // 售后退款金额
  refund_no?: string; // 售后单号
}

/** 各类型订单计数 */
export interface TypeCount {
  car?: number;
  hotel?: number;
  tour?: number;
  guide?: number;
}

/** 分页信息 */
export interface Pagination {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

/** 统一订单列表响应 */
export interface OrderListResponse {
  code: number;
  message: string;
  data: {
    list: OrderListItem[];
    pagination: Pagination;
    type_count?: TypeCount; // 各类型订单数量
  };
}

/** 订单列表查询参数 */
export interface OrderListParams {
  page?: number;
  limit?: number;
  type?: OrderProductType | "";
  /** 状态码（0-7），不传则全部 */
  status?: number;
  sort?: string;
  order?: string;
}

// ==================== 接口请求 ====================

/**
 * 获取全部订单列表（用户端）
 * GET /home/order/all
 * 需认证：Header Authorization
 */
export function getOrderList(params: OrderListParams = {}) {
  return http.get<OrderListResponse>("/home/order/all", { params });
}
